import {
  callStudentEventsApi,
  clearLocalStorageKeys,
  deserify,
  getDeviceType,
  getLocalStorage,
  RouteError,
  useCommonServiceClientContext,
  CommonServiceClientContextProvider,
} from '@geneo2-web/shared-ui';
import { ENTER_UID, HOME } from '../../routeHandling/RoutesNomenclature';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../reduxStore/reduxHooks';
import {
  resetAuthState,
  setIsLoggingOut,
} from '../../pages/Auth/reducer/auth.slice';
import {
  aiBookCloseEvent,
  aiContentCloseEvent,
  aiLessonCloseEvent,
  aiTopicCloseEvent,
  aiChapterCloseEvent,
  aiResourceCloseEvent,
  aiResourcePageCloseEvent,
} from '../../pages/Learn/utils_learn/learn.events';
import {
  resetHomeworkState,
  setActiveHomeworSessionId,
  setStudentHwUploadData,
} from '../../pages/Homework/reducer/homework.slice';
import {
  useDownloadContext,
  DownloadContextProviderV2,
} from '../../app/Context/DownloadContextProviderV2';
import { aiPerformanceCloseEvent } from '../../pages/Performance/utils_performance/performance.events';
import {
  resetPerformanceState,
  setPerformanceId,
} from '../../pages/Performance/reducer/performance.slice';
import {
  aiHomeworkCloseEvent,
  aiHomeworkReviewCloseEvent,
} from '../../pages/Homework/utils_homework/homework.events';
import {
  aiLogoutEvent,
  interactionEvent,
} from '../../pages/Auth/Login/login_logout.events';
import {
  DeviceEnum,
  ProfileRolesEnum,
} from '@protos/user_management/ums.db_pb';
import { resetHomeState } from '../../pages/Home/reducer/homeDashboard.slice';
import { resetLearnState } from '../../pages/Learn/reducer/learn.slice';
import { resetReportData } from '../../pages/Sidebar/Reports/reducer/report.slice';
import { Code, ConnectError, Interceptor } from '@bufbuild/connect';
import { useDispatch } from 'react-redux';
import { resetPalState } from '../../pages/Pal/reducer/pal.slice';
import { resetMultiSubjectFAState } from '../../pages/Homework/MultiSubjectFormativeAssessment/reducer/multisubjectFA.slice';

const RouteErrorBoundaryWithContext = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const android = window.Android;
  const userId = getLocalStorage('userId');
  const fcmToken = getLocalStorage('fcmToken');
  const deviceId = getLocalStorage('deviceID');
  const deviceType = getDeviceType();

  const commonServiceClientContext = useCommonServiceClientContext();
  const {
    UMSLoginAPIServiceV1ClientWithStatusCodeHandler,
    UmsUtilityAPIServiceV1ClientWithStatusCodeHandler,
  } = commonServiceClientContext;
  const { isOffline } = useDownloadContext();
  const { userInfo, is_mcb_user, ssoRedirectionUrl } = deserify(
    useAppSelector((state) => state.auth)
  );
  const { selected_book_id } = deserify(useAppSelector((state) => state.home));
  const { selected_chapter_resources } = deserify(
    useAppSelector((state) => state.learn)
  );
  const {
    selected_module_lessons,
    selected_lesson_content,
    selected_resource_content,
    selected_resource_info,
    selected_resource_session_id,
  } = deserify(useAppSelector((state) => state.learn));

  const { performance_id } = deserify(
    useAppSelector((state) => state.performance)
  );
  const { selected_homework_data, active_homework_session_id } = deserify(
    useAppSelector((state) => state.homework)
  );

  // ---------------------------------------------------------------

  const notificationAPI = async () => {
    if (!userId) {
      console.log('User ID not found.');
      return;
    }
    if (isOffline) return;

    try {
      await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.saveDeviceInfo({
        deviceId: deviceId ? BigInt(deviceId) : undefined,
        profileId: BigInt(userId),
        role: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
        deviceType: DeviceEnum.DEVICE_TYPE_WEB,
        deviceToken: fcmToken?.toString(),
        isActive: false,
      });
      await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.saveDeviceInfo({
        deviceToken: fcmToken?.toString(),
        topic: `${String(userId)}_${ProfileRolesEnum.PROFILE_ROLE_STUDENT}`,
        subscribeTopic: false,
      });
    } catch (err) {
      console.error('Error in Notification API:', err);
    }
  };

  // ---------------------------------------------------------------

  const commonDispatch = async () => {
    dispatch(setActiveHomeworSessionId(undefined));
    dispatch(setStudentHwUploadData([]));
  };

  // ---------------------------------------------------------------

  const commonAiCloseEvents = async (name: string) => {
    await aiContentCloseEvent({
      pageNumber: selected_resource_info?.pageNumber,
      pageContent: selected_resource_info?.pageContent,
      isOffline: isOffline,
    });
    await aiResourcePageCloseEvent({
      resourceId: selected_resource_content?.parsedData.resourceId,
      pageNumber: selected_resource_info?.pageNumber,
      isOffline: isOffline,
    });
    await aiResourceCloseEvent({
      resourceId: selected_resource_content?.parsedData.resourceId,
      isOffline: isOffline,
    });
    await aiLessonCloseEvent({
      lessonId: selected_lesson_content?.lessonId,
      isOffline: isOffline,
    });
    await aiTopicCloseEvent({
      topicId: selected_module_lessons?.moduleId,
      isOffline: isOffline,
    });
    await aiChapterCloseEvent({
      chapterId: selected_chapter_resources?.chapterId,
      isOffline: isOffline,
    });

    await aiPerformanceCloseEvent({
      performanceId: performance_id,
      isOffline: isOffline,
    });
    dispatch(setPerformanceId(undefined));

    await aiHomeworkCloseEvent({
      homeworkId: selected_homework_data?.homeworkId,
      isOffline: isOffline,
    });
    await aiHomeworkReviewCloseEvent({
      homeworkId: selected_homework_data?.homeworkId,
      isOffline: isOffline,
    });

    await interactionEvent({
      url: pathSegments[1].replace(/ /g, '_'),
      context: 'navigation_bar',
      name: name,
      isOffline: isOffline,
    });

    commonDispatch();
  };

  // ---------------------------------------------------------------

  const onLogoutClick = async () => {
    try {
      dispatch(setIsLoggingOut(true));
      await aiBookCloseEvent({
        bookId: selected_book_id,
        isOffline: isOffline,
      });
      await commonAiCloseEvents('SIGN_OUT');
      await aiLogoutEvent({ isOffline: isOffline });
      !isOffline && (await callStudentEventsApi(commonServiceClientContext));
      if (fcmToken) {
        await notificationAPI();
      }
      const response =
        await UMSLoginAPIServiceV1ClientWithStatusCodeHandler.logout({
          profileId: getLocalStorage('userId'),
          role: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
        });
      if (deviceType === 'android') {
        android.userLoggedOut(true);
      }
      if (response) {
        // aiBookCloseEvent(selected_book_id);
        // commonAiCloseEvents();
        // aiLogoutEvent();
      }
    } catch (err) {
      console.log(err);
      dispatch(setIsLoggingOut(false));
    } finally {
      let redirectUrl = undefined;
      if (is_mcb_user && ssoRedirectionUrl) {
        redirectUrl = ssoRedirectionUrl;
      }
      dispatch(resetAuthState());
      dispatch(resetHomeState());
      dispatch(resetLearnState());
      dispatch(resetPerformanceState());
      dispatch(resetHomeworkState());
      dispatch(resetMultiSubjectFAState());
      dispatch(resetReportData());
      dispatch(resetPalState());

      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        navigate(ENTER_UID);
      }
      // localStorage.clear();
      clearLocalStorageKeys();
    }
  };

  // ---------------------------------------------------------------

  return (
    <RouteError
      isLoggedIn={!!userInfo}
      onLogoutClick={onLogoutClick}
      onHomeClick={() => navigate(HOME)}
      onLoginClick={() => navigate(ENTER_UID)}
    />
  );
};

// ===================================================================

// Minimal context provider wrapper for error boundary
const ErrorBoundaryContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch();
  const { is_logged_in_offline } = useAppSelector((state) => state.auth);

  const handleLogout = () => {
    dispatch(resetAuthState());
    dispatch(resetHomeState());
    dispatch(resetLearnState());
    dispatch(resetPerformanceState());
    dispatch(resetHomeworkState());
    dispatch(resetMultiSubjectFAState());
    dispatch(resetReportData());
    dispatch(resetPalState());
    clearLocalStorageKeys();
  };

  const loginInterceptor: Interceptor = (next) => async (req) => {
    if (
      is_logged_in_offline &&
      ![
        'validateLoginSession',
        'validateUser',
        'studentLoginWithUsername',
      ].includes(req.method.name)
    ) {
      throw new ConnectError('Unverified user', Code.NotFound);
    }
    return await next(req);
  };

  const headerInterceptor: Interceptor = (next) => async (req) => {
    if (process.env.NX_GENEO_FRONTEND_VERSION) {
      req.header.append(
        'X-Geneo-Version',
        process.env.NX_GENEO_FRONTEND_VERSION
      );
    }
    req.header.append('X-Application-Type', 'student');
    return await next(req);
  };

  const getResponseStatusCodeFunctions = () => {
    return {
      '401': handleLogout,
    };
  };

  return (
    <CommonServiceClientContextProvider
      responseStatusCodeFunction={getResponseStatusCodeFunctions()}
      interceptors={[loginInterceptor, headerInterceptor]}
    >
      <DownloadContextProviderV2>{children}</DownloadContextProviderV2>
    </CommonServiceClientContextProvider>
  );
};

// ===================================================================

// Main export - wraps in context provider to ensure context is available
export const RouteErrorBoundary = () => {
  return (
    <ErrorBoundaryContextProvider>
      <RouteErrorBoundaryWithContext />
    </ErrorBoundaryContextProvider>
  );
};
